import { createSlice } from '@reduxjs/toolkit';

// Helper function to get token from localStorage with expiry check
const getStoredToken = (key) => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    
    const tokenData = JSON.parse(item);
    
    // Check if token has expired
    if (tokenData.expiry && Date.now() > tokenData.expiry) {
      console.log(`🗑️ ${key} expired, removing from localStorage`);
      localStorage.removeItem(key);
      return null;
    }
    
    return tokenData.token; // Return the token string
  } catch (error) {
    console.error(`❌ Error reading ${key} from localStorage:`, error);
    localStorage.removeItem(key);
    return null;
  }
};

// Get initial state from localStorage
const getInitialState = () => {
  const accessToken = getStoredToken('accessToken');
  const refreshToken = getStoredToken('refreshToken');
  
  // console.log('🔄 Initializing auth state from localStorage:');
  // console.log('   Access Token:', accessToken ? 'Present' : 'Missing');
  // console.log('   Refresh Token:', refreshToken ? 'Present' : 'Missing');
  // console.log('   Is Authenticated:', !!accessToken);
  
  return {
    value: 0,
    refreshToken: refreshToken,
    accessToken: accessToken,
    isAuthenticated: !!accessToken
  };
};

const initialState = getInitialState();

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    increment: (state) => {
      state.value += 1;
    },
    decrement: (state) => {
      state.value -= 1;
    },
    incrementByAmount: (state, action) => {
      state.value += action.payload;
    },
    setRefreshToken: (state, action) => {
      // Ensure we're storing the token string
      const tokenString = typeof action.payload === 'object' ? action.payload.token : action.payload;
      state.refreshToken = tokenString;
      
      // Store with 30 days expiry
      const tokenExpiry = new Date();
      tokenExpiry.setDate(tokenExpiry.getDate() + 30);
      const tokenData = {
        token: tokenString,
        expiry: tokenExpiry.getTime()
      };
      localStorage.setItem("refreshToken", JSON.stringify(tokenData));
      console.log('💾 Refresh token saved with 30-day expiry');
    },
    setAccessToken: (state, action) => {
      // Ensure we're storing the token string
      const tokenString = typeof action.payload === 'object' ? action.payload.token : action.payload;
      state.accessToken = tokenString;
      state.isAuthenticated = true;
      
      // Store with 30 days expiry
      const tokenExpiry = new Date();
      tokenExpiry.setDate(tokenExpiry.getDate() + 30);
      const tokenData = {
        token: tokenString,
        expiry: tokenExpiry.getTime()
      };
      localStorage.setItem("accessToken", JSON.stringify(tokenData));
      console.log('💾 Access token saved with 30-day expiry');
    },
    logout: (state) => {
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      console.log('🚪 User logged out, tokens cleared');
    },
    initializeAuth: (state) => {
      // This will be called on app startup to rehydrate auth state
      const accessToken = getStoredToken('accessToken');
      const refreshToken = getStoredToken('refreshToken');
      
      state.accessToken = accessToken;
      state.refreshToken = refreshToken;
      state.isAuthenticated = !!accessToken;
      
      console.log('🔄 Auth state reinitialized from localStorage');
    }
  },
});

export const {
  increment,
  decrement,
  incrementByAmount,
  setRefreshToken,
  setAccessToken,
  logout,
  initializeAuth,
} = authSlice.actions;

export default authSlice.reducer;